import { useRef, useState } from "react";

import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import SignaturePad from "react-signature-canvas";
import { useDispatch, useSelector } from "react-redux";
import {
  HelpCircle,
  Lock,
  Pencil,
  Shield,
  ShieldCheck,
  Trash2,
  Type,
  Upload,
} from "lucide-react";

import { useToast } from "../../hooks/use-toast";
import { getObjLength } from "../../helpers/utils";
import { Input } from "../../common/customDesignedComponents/input";
import { Label } from "../../common/customDesignedComponents/label";
import bbbBadge from "../../../assets/new_ui_images/bbb-badge.webp";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import soc2Badge from "../../../assets/new_ui_images/soc2-type2-badge.png";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import oneMillionBadge from "../../../assets/new_ui_images/one-million-usd.webp";
import {
  Dialog,
  DialogContent,
} from "../../common/customDesignedComponents/dialog";

const signatureFonts = [
  { name: "Dancing Script", style: "'Dancing Script', cursive" },
  { name: "Great Vibes", style: "'Great Vibes', cursive" },
  { name: "Pacifico", style: "'Pacifico', cursive" },
  { name: "Caveat", style: "'Caveat', cursive" },
];

type SignatureMethod = "draw" | "type" | "upload";

const SignatureV2 = ({
  user_id,
  isAdminCreateUser,
  admin_id,
}: {
  user_id: string;
  isAdminCreateUser: boolean;
  admin_id: string;
}) => {
  const [activeMethod, setActiveMethod] = useState<SignatureMethod>("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedFont, setSelectedFont] = useState(0);
  const [hasSignature, setHasSignature] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [uploadedFileBase64, setUploadedFileBase64] = useState<string>("");

  const signaturePadRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast();

  const crerateAccData = useSelector(
    (state: any) => state.signUpUser.createAccResponse,
  );
  const userIdFromSession = JSON.parse(localStorage.getItem("user_Id"));
  const dispatch = useDispatch();

  // Handle signature pad events
  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  const handleClearSignature = () => {
    if (activeMethod === "draw" && signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasSignature(false);
    } else if (activeMethod === "type") {
      setTypedSignature("");
      setHasSignature(false);
    } else if (activeMethod === "upload") {
      setUploadedFile(null);
      setUploadedFileName("");
      setUploadedFileBase64("");
      setHasSignature(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleTypedSignatureChange = (value: string) => {
    setTypedSignature(value);
    setHasSignature(value.length > 0);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setUploadedFileName(file.name);
      setHasSignature(true);

      // Convert file to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        setUploadedFileBase64(base64String);
        console.log("Uploaded file base64:", base64String);
      };
      reader.onerror = () => {
        toast({
          title: "Error",
          description: "Failed to process the uploaded file.",
          variant: "destructive",
        });
      };
      reader.readAsDataURL(file);

      toast({
        title: "Signature Uploaded",
        description: "Your signature image has been uploaded.",
      });
    }
  };

  const getSignatureBase64 = async (): Promise<string | null> => {
    try {
      if (activeMethod === "draw") {
        // Get base64 from drawn signature
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
          return signaturePadRef.current
            .getTrimmedCanvas()
            .toDataURL("image/png");
        }
      } else if (activeMethod === "type") {
        // Convert typed signature to base64
        if (typedSignature.trim().length > 0) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          if (!ctx) return null;

          // Set canvas size
          canvas.width = 500;
          canvas.height = 200;

          // Set font style
          const fontStyle = signatureFonts[selectedFont].style;
          ctx.font = `48px ${fontStyle}`;
          ctx.fillStyle = "#000000";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          // Draw text
          ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

          return canvas.toDataURL("image/png");
        }
      } else if (activeMethod === "upload") {
        // Convert uploaded file to base64
        if (uploadedFile) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(uploadedFile);
          });
        }
      }
      return null;
    } catch (error) {
      console.error("Error converting signature to base64:", error);
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!hasSignature) {
      toast({
        title: "Signature Required",
        description: "Please create your signature before continuing.",
        variant: "destructive",
      });
      return;
    }

    if (!agreeToTerms) {
      toast({
        title: "Agreement Required",
        description:
          "Please confirm that this is a legal representation of your signature.",
        variant: "destructive",
      });
      return;
    }

    // Get signature in base64 format
    const signatureBase64 = await getSignatureBase64();

    if (signatureBase64) {
      console.log("Signature Base64:", signatureBase64);
      // You can use signatureBase64 here for your API call or state management
      // For example: dispatch(userSigupAction.signBase64Value(signatureBase64));

      // Show success modal
      // setShowSuccessModal(true);
      let data = {
        signature: signatureBase64,
        user_id:
          (getObjLength(crerateAccData)
            ? crerateAccData.data?.user_id
            : userIdFromSession) || user_id,
        //  crerateAccData?.data?.user_id,
        from_admin: isAdminCreateUser ? 1 : undefined,
        admin_id: isAdminCreateUser ? admin_id : undefined,
        step: 7,
        step_key: "REGISTER_SIGNATURE",
      };

      dispatch(
        userSigupAction.getRegisterSignature({
          url: "customer/register-signature",
          data: data,
        }),
      );
    } else {
      toast({
        title: "Error",
        description: "Failed to process signature. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNextStep = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      <Helmet>
        <title>Signature | EquityProtect - Create Your Signature</title>
        <meta
          name="description"
          content="Create your digital signature for your EquityProtect account."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Caveat:wght@400;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Left Column - Info Box */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-32"
            >
              <div className="bg-gradient-teal rounded-2xl p-8 md:p-10 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="h-10 w-10 text-white" />
                  <h2 className="text-2xl md:text-3xl font-display font-medium">
                    Digital Signature
                  </h2>
                </div>

                <p className="text-lg text-white/90 mb-8">
                  Your signature will be used as a legal representation for all
                  documents and authorizations related to your EquityProtect
                  account protection.
                </p>

                <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                  <img
                    src={oneMillionBadge}
                    alt="One Million Dollar Guarantee"
                    className="h-16 w-auto"
                  />
                  <img
                    src={bbbBadge}
                    alt="BBB Accredited Business"
                    className="h-14 w-auto"
                  />
                  <img
                    src={soc2Badge}
                    alt="SOC 2 Certified"
                    className="h-14 w-auto"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right Column - Signature */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
                {/* Secure Connection Bar */}
                <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                  <Lock className="h-4 w-4 text-white" />
                  <span className="text-white font-medium text-sm">
                    Secure connection
                  </span>
                </div>

                <div className="p-6 md:p-8">
                  <h1 className="text-xl md:text-2xl font-display font-medium text-foreground text-center mb-6">
                    Create Signature
                  </h1>

                  {/* Method Tabs */}
                  <div className="flex border-b border-border mb-6">
                    <button
                      onClick={() => setActiveMethod("draw")}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeMethod === "draw"
                          ? "border-secondary text-secondary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Pencil className="w-4 h-4" />
                      Draw it in
                    </button>
                    <button
                      onClick={() => setActiveMethod("type")}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeMethod === "type"
                          ? "border-secondary text-secondary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Type className="w-4 h-4" />
                      Type it in
                    </button>
                    <button
                      onClick={() => setActiveMethod("upload")}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeMethod === "upload"
                          ? "border-secondary text-secondary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      Upload image
                    </button>
                  </div>

                  {/* Signature Area */}
                  <div className="mb-6">
                    {activeMethod === "draw" && (
                      <div className="relative">
                        <div className="border-2 border-dashed border-border rounded-lg overflow-hidden bg-white">
                          <SignaturePad
                            ref={signaturePadRef}
                            onEnd={handleSignatureEnd}
                            canvasProps={{
                              width: 500,
                              height: 200,
                              className: "w-full",
                            }}
                            penColor="#000000"
                            backgroundColor="#ffffff"
                          />
                        </div>
                        {hasSignature && (
                          <button
                            onClick={handleClearSignature}
                            className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-muted transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-muted-foreground" />
                          </button>
                        )}
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          Use your mouse or finger to draw your signature
                        </p>
                      </div>
                    )}

                    {activeMethod === "type" && (
                      <div>
                        <Input
                          value={typedSignature}
                          onChange={(e) =>
                            handleTypedSignatureChange(e.target.value)
                          }
                          placeholder="Type your name"
                          className="h-12 text-lg mb-4"
                        />

                        {/* Signature Preview */}
                        <div className="border-2 border-dashed border-border rounded-lg p-8 bg-white min-h-[120px] flex items-center justify-center mb-4">
                          <span
                            className="text-4xl text-foreground"
                            style={{
                              fontFamily: signatureFonts[selectedFont].style,
                            }}
                          >
                            {typedSignature || "Your Signature"}
                          </span>
                        </div>

                        {/* Font Selection */}
                        <div className="flex flex-wrap gap-2">
                          {signatureFonts.map((font, index) => (
                            <button
                              key={font.name}
                              onClick={() => setSelectedFont(index)}
                              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                                selectedFont === index
                                  ? "border-secondary bg-secondary/10 text-secondary"
                                  : "border-border text-muted-foreground hover:border-secondary/50"
                              }`}
                              style={{ fontFamily: font.style }}
                            >
                              {font.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {activeMethod === "upload" && (
                      <div className="relative">
                        {hasSignature && uploadedFileName ? (
                          <div className="border-2 border-dashed border-border rounded-lg p-6 bg-muted/30">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex-shrink-0 w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                                  <Upload className="w-6 h-6 text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-foreground font-medium text-sm truncate">
                                    {uploadedFileName}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    File uploaded successfully
                                  </p>
                                </div>
                              </div>
                              {hasSignature && (
                                <button
                                  onClick={handleClearSignature}
                                  className="ml-3 p-2 bg-white rounded-full shadow-md hover:bg-muted transition-colors flex-shrink-0"
                                >
                                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                                </button>
                              )}
                            </div>
                            {uploadedFileBase64 && (
                              <div className="mt-4 border border-border rounded-lg overflow-hidden bg-white">
                                <img
                                  src={uploadedFileBase64}
                                  alt="Signature preview"
                                  className="w-full h-auto max-h-[200px] object-contain"
                                />
                              </div>
                            )}
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="mt-4 text-sm text-secondary hover:text-secondary/80 font-medium"
                            >
                              Change file
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-border rounded-lg p-8 bg-muted/30 min-h-[200px] flex flex-col items-center justify-center cursor-pointer hover:border-secondary/50 transition-colors"
                          >
                            <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                            <p className="text-foreground font-medium mb-1">
                              Click to upload your signature
                            </p>
                            <p className="text-sm text-muted-foreground">
                              PNG, JPG up to 5MB
                            </p>
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>

                  {/* Agreement Checkbox */}
                  <div className="flex items-start gap-2 mb-6 p-4 bg-muted/30 rounded-lg">
                    <Checkbox
                      id="agreement"
                      checked={agreeToTerms}
                      onCheckedChange={(checked) =>
                        setAgreeToTerms(checked as boolean)
                      }
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="agreement"
                      className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                    >
                      I understand this is a legal representation of my
                      signature.
                    </Label>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-center">
                    <Button
                      onClick={handleSubmit}
                      disabled={!hasSignature}
                      className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-10 py-2.5 text-sm rounded-full shadow-md hover:shadow-lg transition-all"
                    >
                      INSERT SIGNATURE
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md p-0 gap-0 border-0 overflow-hidden">
          <div className="p-8 text-center">
            <div className="flex justify-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-secondary/20 to-cyan-400/20 flex items-center justify-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary to-cyan-400 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
              </motion.div>
            </div>

            <h2 className="text-2xl font-semibold text-foreground mb-3">
              Signature Saved
            </h2>

            <p className="text-muted-foreground mb-8">
              Your digital signature has been securely saved to your account.
            </p>

            <Button
              onClick={handleNextStep}
              className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-10 py-3 text-base rounded-full shadow-md hover:shadow-lg transition-all"
            >
              NEXT STEP
            </Button>
          </div>

          <div className="border-t border-border py-4 px-6 text-center bg-muted/30">
            <p className="text-muted-foreground text-sm flex items-center justify-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Having trouble?{" "}
              <a
                href="#"
                className="text-secondary hover:text-secondary/80 font-medium"
              >
                Contact support
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SignatureV2;
